import { Box3, Object3D, Vector3 } from "three";
import { DecorItemI } from "../../../../shared";
import { BBoxItem, intersectsBBoxItem } from "../../DecorsList/components/getDecorBboxCollection";
export type RoomSizes = { roomLength: number; roomHeight: number; roomDepth: number };
type Params = {
    currentObject: Object3D;
    position: DecorItemI["position"];
    offset: { x: number; y: number; z: number };
    decorBboxCollection: BBoxItem[];
    decor: Pick<DecorItemI, "surface" | "side" | "surfaceOffset" | "translate">;
    roomSizes: RoomSizes;
};

const getBox = (group: Object3D) => {
    return new Box3().setFromObject(group);
};

type IntersectedBox = {
    box: Box3;
    center: Vector3;
    size: Vector3;
};

export const calcCollisions = ({ currentObject, position, offset, decor, roomSizes, decorBboxCollection }: Params) => {
    const SNAP = 0.05;
    const TRANSLATION_GAP = 0.005;

    const isFrontBack = decor.surface === "wall" && (decor.side === "front" || decor.side === "back");
    const isLeftRight = (decor.surface === "wall" && decor.side === "left") || decor.side === "right";
    const isOnFloor = ["floor", "ceiling"].includes(decor.surface);

    const posYOffset = isOnFloor ? (decor.surfaceOffset ?? 0) / 1000 : 0;

    let correctedPosition: DecorItemI["position"] = [
        position[0] + offset.x,
        position[1] + posYOffset,
        position[2] + offset.z,
    ];
    const spirit = currentObject.clone();

    spirit.position.set(correctedPosition[0], correctedPosition[1], correctedPosition[2]);

    const offsetDirection: Vector3 = new Vector3(0, 0, 0);
    const offsetValue = new Vector3(0, 0, 0);

    const size = new Vector3();
    getBox(spirit).getSize(size);

    const rightRoomLimit = roomSizes.roomLength / 2 - size.x / 2;
    const leftRoomLimit = -roomSizes.roomLength / 2 + size.x / 2;
    const topRoomLimit = roomSizes.roomHeight - size.y / 2;
    const backRoomLimit = roomSizes.roomDepth - size.z / 2;
    const frontRoomLimit = size.z / 2;
    const bottomRoomLimit = size.y / 2;

    if (spirit.position.x > rightRoomLimit) {
        spirit.position.x = rightRoomLimit;
    }
    if (spirit.position.x < leftRoomLimit) {
        spirit.position.x = leftRoomLimit;
    }
    if (spirit.position.y > topRoomLimit) {
        spirit.position.y = topRoomLimit;
    }
    if (spirit.position.y < bottomRoomLimit) {
        spirit.position.y = bottomRoomLimit;
    }
    if (spirit.position.z > backRoomLimit) {
        spirit.position.z = backRoomLimit;
    }
    if (spirit.position.z < frontRoomLimit) {
        spirit.position.z = frontRoomLimit;
    }

    const aabb = getBox(spirit);
    let intersectedBox: IntersectedBox | null = null;

    for (const item of decorBboxCollection) {
        const aabbElem = item.aabb;
        const elemCenter = item.center;

        if (intersectsBBoxItem(aabb, item)) {
            if (item.collisionType === "triangular-prism") {
                return null;
            }

            const intersection = aabb.clone().intersect(aabbElem);
            const intersectionSize = new Vector3();
            intersection.getSize(intersectionSize);

            if (!intersectedBox || (intersectedBox && intersectedBox.size.lengthSq() < intersectionSize.lengthSq())) {
                intersectedBox = { box: aabbElem, size: item.size, center: elemCenter };
            }
        }
    }

    const spiritPosition = spirit.position.clone();

    correctedPosition = [spiritPosition.x, spiritPosition.y, spiritPosition.z];

    if (intersectedBox) {
        const center = new Vector3();
        aabb.getCenter(center);

        const overlapX = size.x / 2;
        const overlapY = size.y / 2;
        const overlapZ = size.z / 2;
        const intersectionMin = aabb.clone().intersect(intersectedBox.box);
        const intersectionSizeMin = new Vector3();
        intersectionMin.getSize(intersectionSizeMin);

        const direction = center.clone().sub(intersectedBox.center);
        const normalizedDirection = new Vector3(Math.sign(direction.x), Math.sign(direction.y), Math.sign(direction.z));

        if (isFrontBack || isOnFloor) {
            if (intersectionSizeMin.x < overlapX) {
                offsetDirection.x = normalizedDirection.x;
                offsetValue.x = intersectionSizeMin.x + TRANSLATION_GAP;
            }
        }
        if (isLeftRight || isOnFloor) {
            if (intersectionSizeMin.z < overlapZ) {
                offsetDirection.z = normalizedDirection.z;
                offsetValue.z = intersectionSizeMin.z + TRANSLATION_GAP;
            }
        }

        if (intersectionSizeMin.y < overlapY && !isOnFloor) {
            offsetDirection.y = normalizedDirection.y;
            offsetValue.y = intersectionSizeMin.y + TRANSLATION_GAP;
        }

        const offsetVectorX = offsetDirection.x
            ? new Vector3(Math.sign(offsetDirection.x), 0, 0).multiplyScalar(offsetValue.x)
            : new Vector3();

        const offsetVectorY = offsetDirection.y
            ? new Vector3(0, Math.sign(offsetDirection.y), 0).multiplyScalar(offsetValue.y)
            : new Vector3();

        const offsetVectorZ = offsetDirection.z
            ? new Vector3(0, 0, Math.sign(offsetDirection.z)).multiplyScalar(offsetValue.z)
            : new Vector3();

        // const isBelow = position[1] < intersectedBox.center.y + intersectedBox.size.y / 2;
        if (isFrontBack) {
            if (offsetVectorX.lengthSq() > offsetVectorY.lengthSq()) {
                const correctedX = spiritPosition.clone().add(offsetVectorX);
                if (correctedX.x < rightRoomLimit && correctedX.x > leftRoomLimit) {
                    spiritPosition.copy(correctedX);
                }
            } else {
                const correctedY = spiritPosition.clone().add(offsetVectorY);
                if (correctedY.y < topRoomLimit && correctedY.y > bottomRoomLimit) {
                    spiritPosition.copy(correctedY);
                }
            }
        }

        if (isLeftRight) {
            if (offsetVectorZ.lengthSq() > offsetVectorY.lengthSq()) {
                const correctedZ = spiritPosition.clone().add(offsetVectorZ);
                if (correctedZ.z < backRoomLimit && correctedZ.z > frontRoomLimit) {
                    spiritPosition.copy(correctedZ);
                }
            } else {
                const correctedY = spiritPosition.clone().add(offsetVectorY);
                if (correctedY.y < topRoomLimit && correctedY.y > bottomRoomLimit) {
                    spiritPosition.copy(correctedY);
                }
            }
        }
        if (isOnFloor) {
            // const isFar = position[2] > intersectedBox.center.z - intersectedBox.size.z / 2;
            if (offsetVectorZ.lengthSq() > offsetVectorX.lengthSq()) {
                const correctedZ = spiritPosition.clone().add(offsetVectorZ);

                if (correctedZ.z < backRoomLimit && correctedZ.z > frontRoomLimit) {
                    spiritPosition.copy(correctedZ);
                }
            } else {
                const correctedX = spiritPosition.clone().add(offsetVectorX);
                if (correctedX.x < rightRoomLimit && correctedX.x > leftRoomLimit) {
                    spiritPosition.copy(correctedX);
                }
            }
        }

        correctedPosition = [spiritPosition.x, spiritPosition.y, spiritPosition.z];

        const centerIntersected = intersectedBox.center;
        const sizeIntersected = intersectedBox.size;

        spirit.position.copy(spiritPosition);
        const aabbMoved = getBox(spirit);
        const centerMoved = new Vector3();
        aabbMoved.getCenter(centerMoved);

        const vecBetweenCenters = centerMoved.clone().sub(centerIntersected);

        const distX = Math.round(Math.abs(vecBetweenCenters.x));
        const sizesX = Math.round(size.x / 2 + sizeIntersected.x / 2);

        const distY = Math.round(Math.abs(vecBetweenCenters.y));
        const sizesY = Math.round(size.y / 2 + sizeIntersected.y / 2);

        const distZ = Math.round(Math.abs(vecBetweenCenters.z));
        const sizesZ = Math.round(size.z / 2 + sizeIntersected.z / 2);

        if (!isOnFloor && distX === sizesX) {
            // Выравнивание по Y
            const topGap = Math.abs(centerIntersected.y + sizeIntersected.y / 2 - centerMoved.y - size.y / 2);
            const bottomGap = Math.abs(centerIntersected.y - sizeIntersected.y / 2 - centerMoved.y + size.y / 2);
            if (topGap < SNAP && topGap < bottomGap) {
                correctedPosition[1] = centerIntersected.y + sizeIntersected.y / 2 - size.y / 2;
            }
            if (bottomGap < SNAP && bottomGap <= topGap) {
                correctedPosition[1] = centerIntersected.y - sizeIntersected.y / 2 + size.y / 2;
            }
        }

        if (isFrontBack && distY === sizesY) {
            // Выравнивание по X
            const leftGap = Math.abs(centerIntersected.x - sizeIntersected.x / 2 - centerMoved.x + size.x / 2);
            const rightGap = Math.abs(centerIntersected.x + sizeIntersected.x / 2 - centerMoved.x - size.x / 2);

            if (leftGap < SNAP && leftGap < rightGap) {
                correctedPosition[0] = centerIntersected.x - sizeIntersected.x / 2 + size.x / 2;
            }

            if (rightGap < SNAP && rightGap <= leftGap) {
                correctedPosition[0] = centerIntersected.x + sizeIntersected.x / 2 - size.x / 2;
            }
        }

        if (isLeftRight && distZ === sizesX) {
            // Выравнивание по Z
            const leftGap = Math.abs(centerIntersected.z - sizeIntersected.z / 2 - centerMoved.z + size.z / 2);
            const rightGap = Math.abs(centerIntersected.z + sizeIntersected.z / 2 - centerMoved.z - size.z / 2);

            if (leftGap < SNAP && leftGap < rightGap) {
                correctedPosition[2] = centerIntersected.z - sizeIntersected.z / 2 + size.z / 2;
            }

            if (rightGap < SNAP && rightGap <= leftGap) {
                correctedPosition[2] = centerIntersected.z + sizeIntersected.z / 2 - size.z / 2;
            }
        }

        if (isOnFloor) {
            if (distZ === sizesZ) {
                // Выравнивание по X
                const leftGap = Math.abs(centerIntersected.x - sizeIntersected.x / 2 - centerMoved.x + size.x / 2);
                const rightGap = Math.abs(centerIntersected.x + sizeIntersected.x / 2 - centerMoved.x - size.x / 2);

                if (leftGap < SNAP && leftGap < rightGap) {
                    correctedPosition[0] = centerIntersected.x - sizeIntersected.x / 2 + size.x / 2;
                }

                if (rightGap < SNAP && rightGap <= leftGap) {
                    correctedPosition[0] = centerIntersected.x + sizeIntersected.x / 2 - size.x / 2;
                }
            }

            if (distX === sizesX) {
                // Выравнивание по Z
                const leftGap = Math.abs(centerIntersected.z - sizeIntersected.z / 2 - centerMoved.z + size.z / 2);
                const rightGap = Math.abs(centerIntersected.z + sizeIntersected.z / 2 - centerMoved.z - size.z / 2);

                if (leftGap < SNAP && leftGap < rightGap) {
                    correctedPosition[2] = centerIntersected.z - sizeIntersected.z / 2 + size.z / 2;
                }

                if (rightGap < SNAP && rightGap <= leftGap) {
                    correctedPosition[2] = centerIntersected.z + sizeIntersected.z / 2 - size.z / 2;
                }
            }
        }
    }
    //проверим еще раз пересечения. если после всех смещений еще что-то пересекается - значит что-то где-то пошло не так

    spirit.position.set(correctedPosition[0], correctedPosition[1], correctedPosition[2]);
    const aabbLast = getBox(spirit);
    for (const item of decorBboxCollection) {
        if (intersectsBBoxItem(aabbLast, item)) {
            return null;
        }
    }
    correctedPosition = [
        correctedPosition[0] - offset.x,
        correctedPosition[1] - posYOffset,
        correctedPosition[2] - offset.z,
    ];
    return correctedPosition;
};
